/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */

'use client';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { isEmpty } from '@wconcept/wconcept-common/utils/baseUtils';
import React, { forwardRef, memo } from 'react';
import * as S from './style';

interface IColumnConfig {
  /** 컬럼 키 */
  key: string;
  /** 컬럼 헤더 텍스트 */
  header: string;
  /** 컬럼 너비 */
  width?: string | number;
  /** 헤더 텍스트 정렬 */
  headerAlign?: 'left' | 'center' | 'right';
  /** 바디 텍스트 정렬 */
  align?: 'left' | 'center' | 'right';
  /** 커스텀 렌더 함수 */
  render?: (value: any, row: any, index: number) => React.ReactNode;
  /** 헤더 패딩 */
  headerPadding?: string;
  /** 바디 패딩 */
  padding?: string;
}

interface IProps {
  /** className */
  className?: string;
  /** 컬럼 설정 */
  columns: IColumnConfig[];
  /** 데이터 배열 */
  data: any[];
  /** 테이블 제목 */
  title?: string;
  /** 부제목 */
  subtitle?: string;
  /** 테이블 하단 노트 */
  footer?: string;
  /** 테두리 표시 여부 */
  bordered?: boolean;
  /** no-data 메시지 */
  noDataMessage?: React.ReactNode;
}

const Table = forwardRef<HTMLTableElement, IProps>(
  (
    {
      className,
      columns,
      data,
      title,
      subtitle,
      footer,
      bordered = true,
      noDataMessage = '데이터가 없습니다.',
    },
    ref
  ) => {
    // 컬럼이 없으면 아예 렌더링하지 않음
    if (isEmpty(columns)) {
      return null;
    }

    const hasData = !isEmpty(data);

    return (
      <S.TableWrapper
        className={classNameBind(['table-wrapper', className || ''])}
      >
        {(title || subtitle) && (
          <S.TableHeader>
            {title && <S.TableTitle>{title}</S.TableTitle>}
            {subtitle && <S.TableSubtitle>{subtitle}</S.TableSubtitle>}
          </S.TableHeader>
        )}
        <S.Table ref={ref} $bordered={bordered}>
          <S.THead>
            <tr>
              {columns.map((column, idx) => (
                <S.TH
                  key={`header-${column.key}-${idx}`}
                  $width={column.width}
                  $align={column.headerAlign}
                  $padding={column.headerPadding}
                >
                  {column.header}
                </S.TH>
              ))}
            </tr>
          </S.THead>
          <S.TBody>
            {hasData ? (
              data.map((row, rowIndex) => (
                <S.TR key={`row-${rowIndex}`}>
                  {columns.map((column, colIndex) => (
                    <S.TD
                      key={`cell-${rowIndex}-${column.key}-${colIndex}`}
                      $align={column.align}
                      $padding={column.padding}
                    >
                      {column.render
                        ? column.render(row[column.key], row, rowIndex)
                        : row[column.key]}
                    </S.TD>
                  ))}
                </S.TR>
              ))
            ) : (
              <S.TR>
                <S.TD
                  className="no-data"
                  colSpan={columns.length}
                  $align="center"
                  $padding="120px 0"
                >
                  {noDataMessage}
                </S.TD>
              </S.TR>
            )}
          </S.TBody>
        </S.Table>
        {footer && <S.TableFooter>{footer}</S.TableFooter>}
      </S.TableWrapper>
    );
  }
);

Table.displayName = 'Table';
export default memo(Table);
